/**
 * 직원 상세 - 연락처 탭 (V55.1)
 *
 * Editable: phoneNumber, address
 *
 * V55.1 변경사항:
 * - 전화번호/주소를 한 줄에 50:50 비율로 배치
 */

import React, { useState } from 'react';
import { Phone, Edit2, Save, X } from 'lucide-react';
import { Employee } from '../../../../shared/types/employee';
import { useEmployeeEditableFieldsMutations } from '../../../../shared/hooks/useEmployeeEditableFieldsMutations';

// V62: 전화번호 형식 자동 포맷
function formatPhoneNumber(value: string): string {
  const numbers = value.replace(/\D/g, '');
  if (numbers.length === 11) {
    return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7, 11)}`;
  }
  if (numbers.length === 10) {
    if (numbers.startsWith('02')) {
      return `${numbers.slice(0, 2)}-${numbers.slice(2, 6)}-${numbers.slice(6, 10)}`;
    }
    return `${numbers.slice(0, 3)}-${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
  }
  if (numbers.length === 9 && numbers.startsWith('02')) {
    return `${numbers.slice(0, 2)}-${numbers.slice(2, 5)}-${numbers.slice(5, 9)}`;
  }
  return numbers;
}

function isValidPhoneNumber(value: string): boolean {
  if (!value) return true;
  const numbers = value.replace(/\D/g, '');
  return numbers.length >= 9 && numbers.length <= 11;
}

interface ContactAddressTabProps {
  employee: Employee;
  canEdit: boolean;
  onRefresh: () => void;
}

export function ContactAddressTab({ employee, canEdit, onRefresh }: ContactAddressTabProps) {
  const [isEditing, setIsEditing] = useState(false);
  // V62: affiliationName은 조직/직무 영역으로 이동, 연락처에서 제거
  const [editedData, setEditedData] = useState({
    phoneNumber: employee.phoneNumber || '',
    address: employee.address || '',
  });
  const { updateEditableFields, isLoading } = useEmployeeEditableFieldsMutations();

  const handleEdit = () => {
    setEditedData({
      phoneNumber: employee.phoneNumber || '',
      address: employee.address || '',
    });
    setIsEditing(true);
  };

  const handleCancel = () => {
    setEditedData({
      phoneNumber: employee.phoneNumber || '',
      address: employee.address || '',
    });
    setIsEditing(false);
  };

  const handleSave = async () => {
    // V62: 전화번호 형식 검증
    if (editedData.phoneNumber && !isValidPhoneNumber(editedData.phoneNumber)) {
      alert('전화번호 형식이 올바르지 않습니다. (9~11자리 숫자)');
      return;
    }
    const success = await updateEditableFields(employee.id, {
      phoneNumber: editedData.phoneNumber,
      address: editedData.address,
    });

    if (success) {
      alert('연락처 정보가 수정되었습니다.');
      setIsEditing(false);
      onRefresh();
    }
  };

  return (
    <div style={containerStyle}>
      {/* 섹션 헤더 */}
      <div style={headerStyle}>
        <Phone size={20} style={{ color: 'var(--color-primary)' }} />
        <h3 style={titleStyle}>연락처</h3>
        {canEdit && !isEditing && (
          <button onClick={handleEdit} style={editButtonStyle}>
            <Edit2 size={16} />
            수정
          </button>
        )}
        {isEditing && (
          <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
            <button onClick={handleCancel} style={cancelButtonStyle} disabled={isLoading}>
              <X size={16} />
              취소
            </button>
            <button onClick={handleSave} style={saveButtonStyle} disabled={isLoading}>
              <Save size={16} />
              {isLoading ? '저장 중...' : '저장'}
            </button>
          </div>
        )}
      </div>

      {/* 연락처 정보 */}
      <div style={gridStyle}>
        {/* 전화번호 */}
        <div>
          <div style={labelStyle}>전화번호</div>
          {!isEditing ? (
            <div style={valueStyle}>{employee.phoneNumber || '-'}</div>
          ) : (
            <input
              type="tel"
              value={editedData.phoneNumber}
              onChange={(e) => setEditedData({ ...editedData, phoneNumber: formatPhoneNumber(e.target.value) })}
              style={inputStyle}
              placeholder="전화번호 입력"
            />
          )}
        </div>

        {/* 주소 - V55.1: gridColumn 제거하여 50% 폭 */}
        <div>
          <div style={labelStyle}>주소</div>
          {!isEditing ? (
            <div style={valueStyle}>{employee.address || '-'}</div>
          ) : (
            <input
              type="text"
              value={editedData.address}
              onChange={(e) => setEditedData({ ...editedData, address: e.target.value })}
              style={inputStyle}
              placeholder="주소 입력"
            />
          )}
        </div>

        {/* V62: 소속은 조직/직무 영역으로 이동 — 연락처에서 제거 */}
      </div>
    </div>
  );
}

// ============================================
// 스타일
// ============================================

const containerStyle: React.CSSProperties = {
  background: 'white',
  borderRadius: '12px',
  padding: '24px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  marginBottom: '20px',
  paddingBottom: '12px',
  borderBottom: '1px solid var(--color-gray-200)',
};

const titleStyle: React.CSSProperties = {
  fontSize: '18px',
  fontWeight: '600',
  color: 'var(--color-gray-800)',
  margin: 0,
};

const editButtonStyle: React.CSSProperties = {
  marginLeft: 'auto',
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  padding: '8px 16px',
  background: 'var(--color-primary)',
  color: 'white',
  border: 'none',
  borderRadius: '6px',
  fontSize: '14px',
  fontWeight: '500',
  cursor: 'pointer',
};

const saveButtonStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  padding: '8px 16px',
  background: 'var(--color-success)',
  color: 'white',
  border: 'none',
  borderRadius: '6px',
  fontSize: '14px',
  fontWeight: '500',
  cursor: 'pointer',
};

const cancelButtonStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  padding: '8px 16px',
  background: 'var(--color-gray-200)',
  color: 'var(--color-gray-700)',
  border: 'none',
  borderRadius: '6px',
  fontSize: '14px',
  fontWeight: '500',
  cursor: 'pointer',
};

// V55.1: 전화번호/주소 50:50 배치
const gridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '20px',
};

const labelStyle: React.CSSProperties = {
  fontSize: '12px',
  fontWeight: '500',
  color: 'var(--color-gray-500)',
  marginBottom: '4px',
};

const valueStyle: React.CSSProperties = {
  fontSize: '14px',
  color: 'var(--color-gray-800)',
  fontWeight: '500',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  fontSize: '14px',
  border: '1px solid var(--color-gray-300)',
  borderRadius: '6px',
  outline: 'none',
};
